import api from './api';

export interface EstadoContableSearchParams {
  fechaDesde: string;
  fechaHasta: string;
  compFechaDesde?: string;
  compFechaHasta?: string;
  detallado?: boolean;
}

export type EstadoResultadosPdfListado = 'periodo' | 'mensual' | 'comparativo';

export interface EstadoContableLinea {
  seccion: string;
  nroRubro: number;
  nomRubro: string;
  nroCuenta: number;
  nomCuenta: string;
  saldoInicial?: number;
  saldoInicialComparativo?: number | null;
  /** Neto del período (base − saldo inicial); situación patrimonial. */
  saldoPeriodo?: number;
  saldoPeriodoComparativo?: number | null;
  montoBase: number;
  montoComparativo: number | null;
  variacionAbsoluta: number | null;
  variacionPorcentual: number | null;
}

export interface EstadoContableSeccionTotal {
  seccion: string;
  totalSaldoInicial?: number;
  totalSaldoPeriodo?: number;
  totalBase: number;
  totalSaldoInicialComparativo?: number | null;
  totalSaldoPeriodoComparativo?: number | null;
  totalComparativo: number | null;
  variacionAbsoluta: number | null;
  variacionPorcentual: number | null;
}

export interface EstadoContableResult {
  fechaDesde: string;
  fechaHasta: string;
  compFechaDesde: string | null;
  compFechaHasta: string | null;
  comparativoActivo: boolean;
  lineas: EstadoContableLinea[];
  totalesPorSeccion: EstadoContableSeccionTotal[];
  totalGeneralSaldoInicial: number;
  totalGeneralSaldoPeriodo: number;
  totalGeneralBase: number;
  totalGeneralSaldoInicialComparativo: number | null;
  totalGeneralSaldoPeriodoComparativo: number | null;
  totalGeneralComparativo: number | null;
  variacionGeneralAbsoluta: number | null;
  variacionGeneralPorcentual: number | null;
}

function unwrapMessage(error: unknown): string {
  const ax = error as { response?: { data?: { message?: string } } };
  const m = ax.response?.data?.message;
  return typeof m === 'string' ? m : 'Error en la operación';
}

async function getResult(path: string, params: EstadoContableSearchParams): Promise<EstadoContableResult> {
  const response = await api.get<EstadoContableResult>(path, {
    params: {
      fechaDesde: params.fechaDesde,
      fechaHasta: params.fechaHasta,
      compFechaDesde: params.compFechaDesde || undefined,
      compFechaHasta: params.compFechaHasta || undefined
    }
  });
  return response.data;
}

async function getPdf(
  path: string,
  params: EstadoContableSearchParams & { listado?: EstadoResultadosPdfListado }
): Promise<Blob> {
  try {
    const response = await api.get<Blob>(path, {
      params: {
        fechaDesde: params.fechaDesde,
        fechaHasta: params.fechaHasta,
        compFechaDesde: params.compFechaDesde || undefined,
        compFechaHasta: params.compFechaHasta || undefined,
        listado: params.listado || undefined,
        detallado: params.detallado ?? undefined
      },
      responseType: 'blob'
    });
    return response.data;
  } catch (err: unknown) {
    throw new Error(unwrapMessage(err));
  }
}

const estadosContablesService = {
  getEstadoResultados: async (params: EstadoContableSearchParams): Promise<EstadoContableResult> =>
    getResult('/api/EstadosContables/estado-resultados', params),

  getEstadoResultadosExportData: async (params: EstadoContableSearchParams): Promise<EstadoContableResult> =>
    getResult('/api/EstadosContables/estado-resultados/export-data', params),

  getEstadoResultadosPdfBlob: async (
    params: EstadoContableSearchParams,
    listado: EstadoResultadosPdfListado = 'periodo'
  ): Promise<Blob> =>
    getPdf('/api/EstadosContables/estado-resultados/preview-pdf', { ...params, listado }),

  getSituacionPatrimonial: async (params: EstadoContableSearchParams): Promise<EstadoContableResult> =>
    getResult('/api/EstadosContables/situacion-patrimonial', params),

  getSituacionPatrimonialExportData: async (params: EstadoContableSearchParams): Promise<EstadoContableResult> =>
    getResult('/api/EstadosContables/situacion-patrimonial/export-data', params),

  getSituacionPatrimonialPdfBlob: async (params: EstadoContableSearchParams): Promise<Blob> =>
    getPdf('/api/EstadosContables/situacion-patrimonial/preview-pdf', params),

  parseError: unwrapMessage
};

export default estadosContablesService;
